import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Calendar, ChevronDown, MapPin, Mail, Clock, Facebook, Instagram } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import { useServices, useLocations } from "../lib/api";
import { RPM_LOGO, ADDRESS } from "../lib/assets";
import { Container, PHONE, PHONE_RAW, BtnPrimary } from "./common";

const NAV = [
 { to: "/", label: "Home" },
 { to: "/about", label: "About" },
 { to: "/services", label: "Services", mega: "services" },
 { to: "/blog", label: "Blog" },
 { to: "/faq", label: "FAQ" },
 { to: "/reviews", label: "Reviews" },
 { to: "/contact", label: "Contact" },
];

function TopBar() {
 return (
 <div className="hidden bg-ink text-xs text-muted-foreground md:block">
 <Container className="flex h-9 items-center justify-between">
 <div className="flex items-center gap-6">
 <span className="flex items-center gap-1.5"><MapPin size={13} className="text-primary" /> {ADDRESS}</span>
 <span className="flex items-center gap-1.5"><Clock size={13} className="text-primary" /> Mon-Fri 8-6 · Sat 8-2</span>
 <a href="mailto:rpmauto2011@gmail.com" className="flex items-center gap-1.5 hover:text-white"><Mail size={13} className="text-primary" /> rpmauto2011@gmail.com</a>
 </div>
 <div className="flex items-center gap-4">
 <span className="font-semibold text-white/80">ASE Master Certified · AAA Approved · Since 1995</span>
 </div>
 </Container>
 </div>
 );
}

function MegaPanel({ type, onNav }) {
 const { data: services = [] } = useServices();
 const { data: locations = [] } = useLocations();
 if (type === "services") {
 const cats = [...new Set(services.map((s) => s.category))];
 if (cats.length === 0) {
 return (
 <div className="p-6">
 <Link to="/services" onClick={onNav} className="text-sm font-semibold text-primary hover:underline" data-testid="mega-services-fallback">
 View all services →
 </Link>
 </div>
 );
 }
 return (
 <div className="grid max-h-[70vh] grid-cols-2 gap-x-8 gap-y-6 overflow-auto p-6 lg:grid-cols-4">
 {cats.map((c) => (
 <div key={c}>
 <div className="mb-2 font-display text-sm tracking-wider text-primary">{c}</div>
 <ul className="space-y-1.5">
 {services.filter((s) => s.category === c).map((s) => (
 <li key={s.slug}>
 <Link to={`/services/${s.slug}`} onClick={onNav}
 className="block text-sm text-muted-foreground transition-colors hover:text-white">{s.name}</Link>
 </li>
 ))}
 </ul>
 </div>
 ))}
 </div>
 );
 }
 return (
 <div className="grid grid-cols-2 gap-x-8 gap-y-2 p-6 sm:grid-cols-3 lg:grid-cols-4">
 {locations.map((l) => (
 <Link key={l.slug} to={`/service-areas/${l.slug}`} onClick={onNav}
 className="flex items-center gap-2 py-1 text-sm text-muted-foreground transition-colors hover:text-white">
 <MapPin size={13} className="text-primary" /> {l.name}
 </Link>
 ))}
 </div>
 );
}

function Header() {
 const [scrolled, setScrolled] = useState(false);
 const [open, setOpen] = useState(false);
 const [mega, setMega] = useState(null);
 const closeTimer = useRef(null);
 const openMega = (type) => { clearTimeout(closeTimer.current); setMega(type || null); };
 const scheduleClose = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setMega(null), 200); };
 const loc = useLocation();
 useEffect(() => { setOpen(false); setMega(null); }, [loc.pathname]);
 useEffect(() => {
 const on = () => setScrolled(window.scrollY > 20);
 window.addEventListener("scroll", on); return () => window.removeEventListener("scroll", on);
 }, []);

 return (
 <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-ink/95 backdrop-blur-md shadow-lg shadow-black/40" : "bg-ink"}`}>
 <TopBar />
 <div className="border-b border-white/10">
 <Container className="flex h-[68px] items-center justify-between">
 <Link to="/" className="flex items-center" data-testid="logo-link" aria-label="RPM Auto Repair home">
 <img src={RPM_LOGO} alt="RPM Auto Repair" className="logo-glow h-12 w-auto sm:h-14" />
 </Link>

 <nav className="hidden items-center gap-1 lg:flex" onMouseLeave={scheduleClose}>
 {NAV.map((n) => (
 <div key={n.to} onMouseEnter={() => openMega(n.mega)} className="relative">
 <NavLink to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
 className={({ isActive }) => `flex items-center gap-1 px-3.5 py-2 text-sm font-semibold tracking-wide transition-colors ${isActive ? "text-primary" : "text-white/80 hover:text-white"}`}>
 {n.label}{n.mega && <ChevronDown size={14} />}
 </NavLink>
 </div>
 ))}
 </nav>

 <div className="hidden items-center gap-3 lg:flex">
 <a href={`tel:${PHONE_RAW}`} className="flex items-center gap-2 font-display text-lg text-white hover:text-primary whitespace-nowrap" data-testid="header-phone">
 <Phone size={16} className="text-primary" /> {PHONE}
 </a>
 <BtnPrimary to="/schedule-appointment" className="px-4 py-2.5" testid="header-schedule">
 <Calendar size={15} /> Book
 </BtnPrimary>
 </div>

 {/* Mobile */}
 <Sheet open={open} onOpenChange={setOpen}>
 <SheetTrigger asChild>
 <button className="lg:hidden text-white p-2" aria-label="Open menu" data-testid="mobile-menu-button"><Menu /></button>
 </SheetTrigger>
 <SheetContent side="right" className="w-[300px] overflow-auto border-white/10 bg-ink p-0 text-white">
 <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
 <SheetDescription className="sr-only">Site navigation links for RPM Auto Repair</SheetDescription>
 <div className="flex items-center justify-between border-b border-white/10 p-4">
 <img src={RPM_LOGO} alt="RPM Auto Repair" className="logo-glow h-11 w-auto" />
 </div>
 <nav className="flex flex-col p-2">
 {NAV.map((n) => (
 <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
 className={({ isActive }) => `rounded-md px-4 py-3 text-sm font-semibold tracking-wide ${isActive ? "bg-primary/15 text-primary" : "text-white/80 hover:bg-white/5"}`}>
 {n.label}
 </NavLink>
 ))}
 <div className="mt-3 space-y-2 border-t border-white/10 p-2 pt-4">
 <a href={`tel:${PHONE_RAW}`} className="flex items-center justify-center gap-2 rounded-md border border-white/20 py-3 text-sm font-semibold text-white">
 <Phone size={15} className="text-primary" /> {PHONE}
 </a>
 <BtnPrimary to="/schedule-appointment" className="w-full" testid="mobile-schedule"><Calendar size={15} /> Schedule</BtnPrimary>
 </div>
 </nav>
 </SheetContent>
 </Sheet>
 </Container>

 {mega && (
 <div className="absolute inset-x-0 top-full hidden border-b border-white/10 bg-ink/98 backdrop-blur-md lg:block"
 onMouseEnter={() => openMega(mega)} onMouseLeave={scheduleClose}>
 <Container><MegaPanel type={mega} onNav={() => setMega(null)} /></Container>
 </div>
 )}
 </div>
 </header>
 );
}

function Footer() {
 const { data: services = [] } = useServices();
 return (
 <footer className="border-t border-white/10 bg-ink">
 <Container className="grid gap-10 py-16 md:grid-cols-2 lg:grid-cols-4">
 <div className="lg:col-span-2">
 <Link to="/" className="inline-flex" aria-label="RPM Auto Repair home">
 <img src={RPM_LOGO} alt="RPM Auto Repair" className="logo-glow h-16 w-auto" />
 </Link>
 <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
 Honest, ASE Master Certified auto repair serving Riverside County since 1995. Big-dealer quality without the big-dealer price, backed by a nationwide warranty.
 </p>
 <div className="mt-6 space-y-2 text-sm text-muted-foreground">
 <a href={`tel:${PHONE_RAW}`} className="flex items-center gap-2 whitespace-nowrap hover:text-white"><Phone size={15} className="text-primary" /> {PHONE}</a>
 <a href="mailto:rpmauto2011@gmail.com" className="flex items-center gap-2 hover:text-white"><Mail size={15} className="text-primary" /> rpmauto2011@gmail.com</a>
 <span className="flex items-center gap-2"><MapPin size={15} className="text-primary" /> {ADDRESS}</span>
 <span className="flex items-center gap-2"><Clock size={15} className="text-primary" /> Mon-Fri 8-6 · Sat 8-2 · Sun Closed</span>
 </div>
 <div className="mt-5 flex gap-3">
 <a href="#" aria-label="Facebook" className="grid h-9 w-9 place-items-center rounded-md border border-white/15 text-white/70 hover:border-primary hover:text-primary"><Facebook size={16} /></a>
 <a href="#" aria-label="Instagram" className="grid h-9 w-9 place-items-center rounded-md border border-white/15 text-white/70 hover:border-primary hover:text-primary"><Instagram size={16} /></a>
 </div>
 </div>
 <FooterCol title="Services" links={services.slice(0, 8).map((s) => ({ to: `/services/${s.slug}`, label: s.name }))} more={{ to: "/services", label: "All Services" }} />
 <FooterCol title="Company" links={[
 { to: "/about", label: "About Us" }, { to: "/warranty", label: "Warranty" },
 { to: "/financing", label: "Financing" }, { to: "/reviews", label: "Reviews" },
 { to: "/blog", label: "Blog" }, { to: "/faq", label: "FAQ" },
 { to: "/review", label: "Leave a Review" },
 { to: "/contact", label: "Contact" }, { to: "/schedule-appointment", label: "Book Appointment" },
 ]} />
 </Container>
 <div className="border-t border-white/10">
 <Container className="flex flex-col items-center justify-between gap-3 py-5 text-xs text-muted-foreground sm:flex-row">
 <p>© {new Date().getFullYear()} RPM Auto Repair · NAPA AutoCare Center · ASE Master Certified · AAA Approved · BAR Licensed #265224</p>
 <div className="flex flex-wrap items-center justify-center gap-4">
 <a href="https://www.claritymerchant.com" target="_blank" rel="noopener" className="hover:text-white">Secure payments powered by Clarity Merchant</a>
 <Link to="/privacy-policy" className="hover:text-white">Privacy</Link>
 <Link to="/terms" className="hover:text-white">Terms</Link>
 <Link to="/sitemap" className="hover:text-white">Sitemap</Link>
 </div>
 </Container>
 </div>
 </footer>
 );
}

const FooterCol = ({ title, links, more }) => (
 <div>
 <h4 className="font-display text-sm tracking-wider text-white">{title}</h4>
 <ul className="mt-4 space-y-2">
 {links.map((l) => (
 <li key={l.to}><Link to={l.to} className="text-sm text-muted-foreground hover:text-primary">{l.label}</Link></li>
 ))}
 {more && <li><Link to={more.to} className="text-sm font-semibold text-primary hover:underline">{more.label} →</Link></li>}
 </ul>
 </div>
);

function StickyMobileCta() {
 return (
 <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-px border-t border-white/10 bg-ink/95 backdrop-blur-md lg:hidden" data-testid="sticky-mobile-cta">
 <a href={`tel:${PHONE_RAW}`} className="flex items-center justify-center gap-2 py-3.5 text-sm font-semibold text-white">
 <Phone size={16} className="text-primary" /> Call
 </a>
 <Link to="/schedule-appointment" className="flex items-center justify-center gap-2 bg-primary py-3.5 text-sm font-semibold text-white">
 <Calendar size={16} /> Book Now
 </Link>
 </div>
 );
}

export default function Layout({ children }) {
 return (
 <div className="flex min-h-screen flex-col">
 <Header />
 <main className="flex-1 pb-16 lg:pb-0">{children}</main>
 <Footer />
 <StickyMobileCta />
 </div>
 );
}
